// Session: Edit-Fenster (Spuren, Akkordspur, Timeline), MIDI-Editor, Aufnahme und Takes, Raum mit den anderen Writern.
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useStudio } from '../state/store.ts';
import { engine } from '../audio/engine.ts';
import { uid } from '../state/demo.ts';
import * as op from '../edit/ops.ts';
import { Panel, toast } from '../components/ui.tsx';
import { mediaStore } from '../collab/rtc.ts';
import { toggleRecord } from '../components/Transport.tsx';
import { initials } from '../components/Topbar.tsx';
import { drawWave } from '../audio/waveform.ts';
import { PianoRoll } from './PianoRoll.tsx';
import { TuneEditor } from './TuneEditor.tsx';
import { InputSelect } from './Dante.tsx';
import { Editor, fmtBBT, fmtLen, markerColor } from './Editor.tsx';
import { PluginWindow } from './PluginWindow.tsx';
import { useEdit } from '../state/edit.ts';
import { clipEnd as opClipEnd } from '../edit/ops.ts';
import { removeTakes, run } from '../edit/commands.ts';
import { deleteGroup } from '../edit/mixops.ts';
import { EditDialogs } from './EditDialogs.tsx';

export function Session() {
  const [editing, setEditing] = useState<string | null>(null);
  const close = useCallback(() => setEditing(null), []);
  const view = useEdit((s) => s.view);
  const win = useStudio((s) => s.pluginWin);
  const mixerOpen = useStudio((s) => s.mixerOpen);
  const editKind = useStudio((s) => s.project.clips.find((c) => c.id === editing)?.kind);
  return (
    <div className={`sess ${view.side ? '' : 'noside'}`}>
      <div className={`sess-l ${editing ? 'editing' : ''} ${view.dockClosed && !editing ? 'dock-closed' : ''}`}>
        <Editor onEdit={setEditing} editing={editing} />
        {editing ? (editKind === 'audio' ? <TuneEditor key={editing} clipId={editing} onClose={close} /> : <PianoRoll key={editing} clipId={editing} onClose={close} />) : view.dockClosed ? <DockBar /> : <Takes />}
      </div>
      {view.side ? <Sidebar side={view.side} onEdit={setEditing} /> : <SideRail />}
      <EditDialogs />
      {win && !mixerOpen && <PluginWindow key={win.slotId} />}
    </div>
  );
}

// ---- Dock unten eingeklappt ----
function DockBar() {
  return (
    <section className="panel dockbar" style={{ padding: '6px 10px' }}>
      <span className="h3">Aufnahme und Takes</span>
      <span className="t2" style={{ fontSize: 12, marginLeft: 8 }}>Doppelklick auf einen MIDI-Clip öffnet hier den MIDI-Editor, auf einen Audio-Clip das Vocal-Tuning.</span>
      <button className="btn small" style={{ marginLeft: 'auto' }} onClick={() => useEdit.getState().setView({ dockClosed: false })}>Einblenden ▴</button>
    </section>
  );
}

// ---- Seitenleiste: Raum, Clip-Liste, Memory Locations ----
const SIDE_TABS: { id: 'room' | 'clips' | 'markers' | 'groups'; label: string }[] = [{ id: 'room', label: 'Raum' }, { id: 'clips', label: 'Clips' }, { id: 'markers', label: 'Marker' }, { id: 'groups', label: 'Gruppen' }];
function SideRail() {
  return (
    <section className="panel siderail">
      {SIDE_TABS.map((t) => <button key={t.id} title={`${t.label} zeigen`} onClick={() => useEdit.getState().setView({ side: t.id })}>{t.label}</button>)}
    </section>
  );
}
function Sidebar({ side, onEdit }: { side: 'room' | 'clips' | 'markers' | 'groups'; onEdit: (id: string) => void }) {
  return (
    <section className="panel sidebar">
      <div className="sidetabs">
        {SIDE_TABS.map((t) => <button key={t.id} className={`tab ${side === t.id ? 'on' : ''}`} onClick={() => useEdit.getState().setView({ side: t.id })}>{t.label}</button>)}
        <button className="lnk col" title="Seitenleiste einklappen" onClick={() => useEdit.getState().setView({ side: null })}>»</button>
      </div>
      {side === 'room' ? <RoomPanel /> : side === 'clips' ? <ClipList onEdit={onEdit} /> : side === 'groups' ? <GroupList /> : <MarkerList />}
    </section>
  );
}

/** Clip-Liste (Pro Tools: Clips List): alle Clips des Songs, Klick wählt aus und springt hin */
/** Gruppenliste (Pro Tools: Groups): Mix- und Edit-Gruppen ein- und ausschalten, umbenennen, auflösen */
function GroupList() {
  const p = useStudio((s) => s.project);
  const commit = useStudio((s) => s.commit);
  const groups = p.groups ?? [];
  const upd = (id: string, label: string, f: (g: NonNullable<typeof p.groups>[number]) => void) => commit(label, (q) => { const g = q.groups?.find((x) => x.id === id); if (g) f(g); });
  return (
    <div className="grplist">
      <div className="row" style={{ padding: '10px 12px 6px', gap: 6 }}>
        <button className="btn small primary" title="Spuren mit ⌘-Klick auf die Spurköpfe auswählen, dann ⌘G" onClick={() => run('group')}>+ Gruppe aus Auswahl</button>
        <span className="t2" style={{ fontSize: 11.5 }}>⌘G</span>
      </div>
      {groups.map((g) => (
        <div key={g.id} className={`grp ${g.active ? '' : 'off'}`}>
          <i style={{ background: g.color }} />
          <input value={g.name} onChange={(e) => upd(g.id, 'Gruppe umbenennen', (x) => { x.name = e.target.value; })} title="Name" />
          <button className={`chip ${g.mix ? 'on' : ''}`} title="Mix: Fader (relativ), Mute und Solo gemeinsam" onClick={() => upd(g.id, 'Gruppe Mix', (x) => { x.mix = !x.mix; })}>Mix</button>
          <button className={`chip ${g.edit ? 'on' : ''}`} title="Edit: Auswahl, Schneiden und Verschieben gemeinsam" onClick={() => upd(g.id, 'Gruppe Edit', (x) => { x.edit = !x.edit; })}>Edit</button>
          <button className={`chip ${g.active ? 'on' : ''}`} title="Gruppe vorübergehend aus (Mitglieder einzeln bearbeiten)" onClick={() => upd(g.id, g.active ? 'Gruppe aus' : 'Gruppe an', (x) => { x.active = !x.active; })}>{g.active ? 'an' : 'aus'}</button>
          <button className="lnk" title="Gruppe auflösen (Spuren bleiben)" onClick={() => commit('Gruppe auflösen', (q) => deleteGroup(q, g.id))}>✕</button>
          <div className="grp-tracks" onClick={() => useEdit.getState().setRange({ start: engine.position, end: engine.position, trackIds: g.trackIds })} title="Mitglieder auswählen">{g.trackIds.map((id) => p.tracks.find((t) => t.id === id)?.name).filter(Boolean).join(', ')}</div>
        </div>
      ))}
      {!groups.length && <div className="t2" style={{ padding: 14, fontSize: 12.5, lineHeight: 1.5 }}>Noch keine Gruppen. Spuren auswählen (⌘-Klick auf die Spurköpfe), dann ⌘G. In einer Gruppe bewegen sich Fader gemeinsam (relativ), Mute und Solo gelten für alle, Auswahl und Schnitte auch.</div>}
    </div>
  );
}

function ClipList({ onEdit }: { onEdit: (id: string) => void }) {
  const project = useStudio((s) => s.project);
  const selIds = useStudio((s) => s.selection.clipIds);
  const [q, setQ] = useState('');
  const [onlySel, setOnlySel] = useState(false);
  const tracks = project.tracks.filter((t) => t.kind !== 'bus');
  const clips = project.clips.filter((c) => (!q || c.name.toLowerCase().includes(q.toLowerCase())) && (!onlySel || c.trackId === useStudio.getState().selection.trackId)).sort((a, b) => a.start - b.start);
  const pick = (id: string, dbl = false) => {
    const c = project.clips.find((x) => x.id === id); if (!c) return;
    useStudio.getState().select([c.id], c.trackId);
    useEdit.getState().setRange({ start: c.start, end: opClipEnd(c), trackIds: [c.trackId] });
    useStudio.getState().setTransport({ position: c.start });
    window.dispatchEvent(new CustomEvent('srs:scrollTo', { detail: Math.max(0, c.start - 1) }));
    if (dbl && c.kind === 'midi') onEdit(c.id);
  };
  return (
    <div className="cliplist">
      <div className="clh"><input className="input" placeholder="Clips suchen …" value={q} onChange={(e) => setQ(e.target.value)} style={{ padding: '4px 8px', fontSize: 12 }} />
        <button className={`btn small ${onlySel ? 'on' : ''}`} title="Nur Clips der ausgewählten Spur" onClick={() => setOnlySel(!onlySel)}>Spur</button></div>
      <div className="clh"><span>{clips.length} Clips</span><span style={{ marginLeft: 'auto' }}>Position · Länge</span></div>
      {clips.map((c) => {
        const t = tracks.find((x) => x.id === c.trackId);
        return (
          <div key={c.id} className={`cl ${selIds.includes(c.id) ? 'on' : ''}`} onClick={() => pick(c.id)} onDoubleClick={() => pick(c.id, true)} title={c.kind === 'midi' ? 'Doppelklick öffnet den MIDI-Editor' : 'Klick wählt den Clip aus'}>
            <i style={{ background: t?.color ?? '#8a93a6' }} />
            <div style={{ minWidth: 0 }}><b>{c.name}</b><small>{t?.name ?? '–'} · {c.kind === 'midi' ? `MIDI, ${c.notes?.length ?? 0} Noten` : 'Audio'}{c.by.ai ? ' · KI' : ''}</small></div>
            <small>{fmtBBT(project, c.start)}<br />{fmtLen(project, c.duration, c.start)}</small>
          </div>
        );
      })}
    </div>
  );
}

/** Memory Locations: Marker der Reihe nach, Klick springt hin, Doppelklick wählt den Abschnitt */
function MarkerList() {
  const project = useStudio((s) => s.project);
  const ms = [...project.markers].sort((a, b) => a.time - b.time);
  const section = (i: number) => {
    const m = ms[i], end = m.end ?? ms[i + 1]?.time ?? Math.max(...project.clips.map(opClipEnd), m.time + 8);
    useEdit.getState().setRange({ start: m.time, end, trackIds: project.tracks.filter((t) => t.kind !== 'bus' && !t.hidden).map((t) => t.id) });
    useStudio.getState().setTransport({ position: m.time });
  };
  return (
    <div className="cliplist mlist">
      <div className="clh"><span>{ms.length} Marker</span>
        <button className="lnk" style={{ marginLeft: 'auto' }} onClick={() => run('marker')}>+ Marker an der Position</button>
        <button className="lnk" onClick={() => useEdit.getState().open('markers')}>Bearbeiten</button></div>
      {ms.map((m, i) => (
        <div key={m.id} className="ml" onClick={() => { engine.locate(m.time); window.dispatchEvent(new CustomEvent('srs:scrollTo', { detail: Math.max(0, m.time - 1) })); }} onDoubleClick={() => section(i)} title="Klick springt hin, Doppelklick wählt den Abschnitt auf allen Spuren">
          <span className="n">{i + 1}</span><i style={{ background: markerColor(m) }} /><b style={{ fontWeight: 600 }}>{m.name}</b><small>{fmtBBT(project, m.time)}</small>
        </div>
      ))}
      {!ms.length && <div className="t2" style={{ padding: 14 }}>Noch keine Marker. Taste M setzt einen an der Abspielposition.</div>}
    </div>
  );
}

// ---- Aufnahme und Takes ----
/** Aufnahme-Optionen wie im Pro Tools Transport (Kap. 22): Klick, Vorzähler, Pre-/Post-Roll, Loop-Aufnahme, MIDI Merge, Input Quantize */
function RecOptions() {
  const t = useStudio((s) => s.transport);
  const set = useStudio((s) => s.setTransport);
  const range = useEdit((s) => s.range);
  const sel = range && range.end - range.start > 0.05;
  const loopOn = useStudio((s) => s.project.loop.enabled);
  const mode = t.loopRecord || loopOn ? 'Loop-Aufnahme: läuft im Kreis, jeder Durchgang wird ein Take' : sel ? 'Punch-In/-Out: nur die Auswahl wird ersetzt, dann Stopp' : 'Aufnahme ab der Einfügemarke';
  const bars = (v: number | undefined, k: 'preRoll' | 'postRoll') => (
    <select className="input" value={v ?? 0} onChange={(e) => set({ [k]: Number(e.target.value) })} title={k === 'preRoll' ? 'Vorlauf vor der Aufnahme' : 'Nachlauf nach dem Punch-Out'}>
      {[0, 1, 2, 4].map((n) => <option key={n} value={n}>{n === 0 ? 'aus' : `${n} Takt${n > 1 ? 'e' : ''}`}</option>)}
    </select>
  );
  return (
    <div className="recopts">
      <button className={`chip ${t.metronome ? 'on' : ''}`} onClick={() => set({ metronome: !t.metronome })} title="Klick (K)">Klick</button>
      <button className={`chip ${t.countIn ? 'on' : ''}`} onClick={() => set({ countIn: !t.countIn })} title="Ein Takt Vorzähler vor dem Start">Vorzähler</button>
      <label>Pre-Roll {bars(t.preRoll, 'preRoll')}</label>
      <label>Post-Roll {bars(t.postRoll, 'postRoll')}</label>
      <button className={`chip ${t.loopRecord || loopOn ? 'on' : ''}`} onClick={() => { if (t.loopRecord || loopOn) { set({ loopRecord: false }); if (loopOn) useStudio.getState().commit('Loop aus', (p) => { p.loop.enabled = false; }); } else set({ loopRecord: true }); }} title="Loop-Aufnahme (⌥L, oder Loop-Schalter L an): Auswahl bzw. Loop-Bereich wiederholt aufnehmen, jeder Durchgang ein Take">Loop-Aufnahme</button>
      <span className="sep" />
      <button className={`chip ${t.midiMerge ? 'on' : ''}`} onClick={() => set({ midiMerge: !t.midiMerge })} title="MIDI Merge: neue Noten zu vorhandenen hinzufügen statt sie zu ersetzen">MIDI Merge</button>
      <button className={`chip ${t.inputQuantize ? 'on' : ''}`} onClick={() => set({ inputQuantize: !t.inputQuantize })} title="Input Quantize: MIDI-Noten beim Aufnehmen aufs Raster ziehen">Input Quantize</button>
      <span className="mode t2">{mode}</span>
    </div>
  );
}

function Takes() {
  const project = useStudio((s) => s.project);
  const sel = useStudio((s) => s.selection);
  const t = useStudio((s) => s.transport);
  const set = useStudio((s) => s.setTransport);
  const commit = useStudio((s) => s.commit);
  const audioTracks = project.tracks.filter((x) => x.kind === 'audio');
  const track = audioTracks.find((x) => x.id === sel.trackId) ?? audioTracks.find((x) => x.armed) ?? audioTracks[0];
  const takes = project.takes.filter((x) => x.trackId === track?.id);
  const active = project.clips.find((c) => c.trackId === track?.id && c.takeId)?.takeId;

  const chooseTake = (takeId: string) => commit('Take wählen', (p) => {
    const tk = p.takes.find((x) => x.id === takeId)!;
    const c = p.clips.find((x) => x.trackId === tk.trackId && x.takeId && Math.abs(x.start - tk.start) < 0.5);
    if (c) { c.takeId = tk.id; c.sourceId = tk.sourceId; c.start = tk.start; c.offset = tk.offset ?? 0; c.duration = tk.duration; c.name = `${track!.name} Take ${tk.n}`; }
    else p.clips.push({ id: uid('c'), trackId: tk.trackId, kind: 'audio', name: `${track!.name} Take ${tk.n}`, start: tk.start, offset: tk.offset ?? 0, duration: tk.duration, sourceId: tk.sourceId, takeId: tk.id, fadeIn: 0.005, fadeOut: 0.01, by: { userId: tk.by, at: tk.createdAt } });
  });

  const unused = track ? op.unusedTakes(project, track.id) : [];
  return (
    <Panel title="Aufnahme und Takes" right={<>
      <select className="input" value={track?.id ?? ''} onChange={(e) => useStudio.getState().select([], e.target.value)} style={{ padding: '4px 8px' }}>
        {audioTracks.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
      </select>
      {track && <InputSelect t={track} />}
      <button className="btn small" title="Interface, Latenz und Bridge einrichten" onClick={() => useStudio.getState().setView('dante')}>Audio-Setup</button>
      {unused.length > 0 && <button className="btn small" title="Alle Takes dieser Spur löschen, die nirgends in der Timeline verwendet werden" onClick={() => removeTakes(unused.map((x) => x.id), `${unused.length} unbenutzte ${unused.length === 1 ? 'Take' : 'Takes'}`)}>Unbenutzte löschen ({unused.length})</button>}
      {track && !track.armed && <button className="btn small" onClick={() => commit('Scharf schalten', (p) => { p.tracks.find((x) => x.id === track.id)!.armed = true; })}>{track.name} scharf schalten</button>}
      <button className={`btn small ${t.recording ? 'danger' : 'primary'}`} onClick={() => { if (!project.tracks.some((x) => x.armed)) return toast('Erst eine Spur mit R scharf schalten'); void toggleRecord(); }}>{t.recording ? '■ Stopp' : '● Aufnehmen'}</button>
      <button className="lnk" title="Dock einklappen" onClick={() => useEdit.getState().setView({ dockClosed: true })}>▾</button>
    </>}>
      <RecOptions />
      {takes.length === 0
        ? <div className="t2" style={{ padding: '12px 16px 16px' }}>Noch keine Takes auf {track?.name ?? 'dieser Spur'}. Eingang wählen (In 1, In 2 oder Stereo von deinem Interface, oder ein Kanal der Bridge), Spur mit <b>R</b> scharf schalten, dann <b>Aufnehmen</b> oder Taste R. Aufgenommen wird verlustfrei, die Latenz wird ausgeglichen.</div>
        : <div className="takes">
          {takes.map((tk) => (
            <TakeRow key={tk.id} n={tk.n} who={tk.by} source={tk.source} latency={tk.latencyMs} sourceId={tk.sourceId} offset={tk.offset ?? 0} dur={tk.duration} on={tk.id === active} onClick={() => chooseTake(tk.id)} onDelete={() => removeTakes([tk.id], `Take ${tk.n}`)} />
          ))}
        </div>}
    </Panel>
  );
}

function TakeRow({ n, who, source, latency, sourceId, offset, dur, on, onClick, onDelete }: { n: number; who: string; source: string; latency?: number; sourceId?: string; offset: number; dur: number; on: boolean; onClick: () => void; onDelete: () => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const me = useStudio((s) => s.user);
  const peers = useStudio((s) => s.peers);
  const person = who === me.id ? me : peers.find((p) => p.user.id === who)?.user;
  useEffect(() => {
    const c = ref.current, buf = sourceId ? engine.buffers.get(sourceId) : undefined;
    if (!c) return;
    const W = c.clientWidth, H = c.clientHeight, dpr = devicePixelRatio || 1;
    c.width = W * dpr; c.height = H * dpr;
    const g = c.getContext('2d')!; g.scale(dpr, dpr);
    g.fillStyle = on ? '#003399' : '#c86a7a';
    if (!buf) { g.fillRect(0, H / 2, W, 1); return; }
    drawWave(g, buf, offset, Math.min(dur, buf.duration - offset), 0, W, H / 2, (H - 4) / 2);
  }, [sourceId, on, offset, dur]);
  return (
    <>
      <div className="who"><span className="av" style={{ width: 20, height: 20, fontSize: 8, margin: 0, border: 0, background: person?.color ?? '#5a6478' }}>{initials(person?.name ?? '?')}</span>Take {n}<small>{source === 'bridge' ? 'Dante' : 'Browser'}{latency ? `, ${latency} ms` : ''}</small><button className="tkdel" title={`Take ${n} löschen (auch aus der Timeline)`} onClick={(e) => { e.stopPropagation(); onDelete(); }}>✕</button></div>
      <div className={`take-strip ${on ? 'on' : ''}`} onClick={onClick} title="Diesen Take verwenden"><canvas ref={ref} /></div>
    </>
  );
}

// ---- Raum ----
function RoomPanel() {
  const user = useStudio((s) => s.user);
  const peers = useStudio((s) => s.peers);
  const online = useStudio((s) => s.online);
  const project = useStudio((s) => s.project);
  const media = useSyncExternalStore(mediaStore.subscribe, mediaStore.get);
  // Eigene Person nur einmal (die Anwesenheit kann sie zusätzlich melden, z. B. aus einem zweiten Tab)
  const people = [{ user, latencyMs: undefined as number | undefined }, ...peers.filter((x, i, a) => x.user.id !== user.id && a.findIndex((y) => y.user.id === x.user.id) === i)];
  const tint = (c: string) => `linear-gradient(160deg, ${c}26, ${c}0a)`;

  const contrib = new Map<string, { n: number; label: string; last: string }>();
  for (const c of project.clips) {
    const k = c.by.ai ? 'srs-engine' : c.by.userId;
    const e = contrib.get(k) ?? { n: 0, label: '', last: c.by.at };
    e.n++; e.label = c.name; if (c.by.at > e.last) e.last = c.by.at;
    contrib.set(k, e);
  }
  const nameOf = (id: string) => id === 'srs-engine' ? 'SRS Engine' : id === 'u-gast' ? 'Beispiel' : id === user.id ? user.name : peers.find((p) => p.user.id === id)?.user.name ?? 'Writer';
  const colorOf = (id: string) => id === 'srs-engine' ? '#6b4fbb' : id === user.id ? user.color : peers.find((p) => p.user.id === id)?.user.color ?? '#8a93a6';

  return (
    <>
      <div style={{ overflow: 'auto', flex: 1 }}>
        <div className="row" style={{ padding: '10px 14px 0', gap: 8 }}><b className="h3" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{project.name}</b><span className={`pill ${online.collab ? 'green' : ''}`} style={{ marginLeft: 'auto' }}><i className="ldot" style={{ color: online.collab ? 'var(--green)' : undefined }} />{online.collab ? `${people.length} im Raum` : 'nicht verbunden'}</span></div>
        <div className="room-tiles">
          {people.map((p) => {
            const s = media.streams[p.user.id];
            return (
              <div key={p.user.id} className={`tile ${p.user.id === user.id ? 'me' : ''}`} style={{ background: tint(p.user.color) }}>
                {s ? <TileVideo stream={s} muted={p.user.id === user.id} /> : <div className="big" style={{ background: p.user.color }}>{initials(p.user.name)}</div>}
                <div className="lbl"><span>{p.user.name}{p.user.city ? `, ${p.user.city}` : ''}</span>{p.latencyMs !== undefined && <em>{p.latencyMs} ms</em>}</div>
              </div>
            );
          })}
        </div>
        <div className="room-btns">
          <button className={`btn small ${media.room && media.mic ? 'on' : ''}`} onClick={() => media.room ? mediaStore.toggle('mic') : toast('Erst Video starten')}>Mikro</button>
          <button className={`btn small ${media.room && media.cam ? 'on' : ''}`} onClick={() => media.room ? mediaStore.toggle('cam') : toast('Erst Video starten')}>Kamera</button>
          <button className="btn small" title="Gedrückt halten" onPointerDown={() => media.room && !media.mic && mediaStore.toggle('mic')} onPointerUp={() => media.room && media.mic && mediaStore.toggle('mic')}>Talkback</button>
          {media.room
            ? <button className="btn small" onClick={() => mediaStore.stop()}>Video aus</button>
            : <button className="btn small primary" disabled={!online.collab} title={online.collab ? '' : 'Erst unter Camp einen Raum betreten'} onClick={() => void mediaStore.start(project.roomId ?? project.id)}>Video an</button>}
        </div>
        {!online.collab && <div className="t2" style={{ padding: '4px 16px', fontSize: 12 }}>Unter <b>Camp</b> anmelden und den Raum betreten, dann seht ihr euch hier und arbeitet live am selben Song.</div>}
        <div className="sect">Beiträge, automatisch protokolliert</div>
        <div className="feed">
          {[...contrib.entries()].map(([id, e]) => (
            <div className="it" key={String(id)}>
              <i style={{ background: colorOf(id) }} /><b style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nameOf(id)}</b>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.n} {e.n === 1 ? 'Clip' : 'Clips'} · {e.label}</span>
              <small>{new Date(e.last).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</small>
            </div>
          ))}
        </div>
        <History />
      </div>
    </>
  );
}

function History() {
  const past = useStudio((s) => s.history.past.length);
  const future = useStudio((s) => s.history.future.length);
  return (
    <div className="row" style={{ padding: '12px 16px' }}>
      <button className="btn small" disabled={!past} onClick={() => useStudio.getState().undo()}>↶ Rückgängig</button>
      <button className="btn small" disabled={!future} onClick={() => useStudio.getState().redo()}>↷ Wiederholen</button>
      <span className="muted mono" style={{ fontSize: 11, marginLeft: 'auto' }}>{past} Schritte</span>
    </div>
  );
}

function TileVideo({ stream, muted }: { stream: MediaStream; muted: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => { if (ref.current) ref.current.srcObject = stream; }, [stream]);
  return <video ref={ref} autoPlay playsInline muted={muted} />;
}
